"use client"

import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  FiLogOut, FiChevronLeft, FiChevronRight, FiBriefcase, FiList, 
  FiCheckSquare, FiFolder, FiUsers, FiBox, FiChevronDown, FiChevronUp,
  FiShield, FiActivity, FiSettings, FiPieChart, FiDatabase, FiHome
} from "react-icons/fi";
import { 
  FaChartLine, FaUsers, FaUserTie, FaFileAlt, FaUserCheck, FaWarehouse, 
  FaMoneyBill, FaGavel, FaFile, FaUserCircle, FaFolderOpen, FaTachometerAlt,
  FaTasks, FaUpload, FaMoneyBillWave, FaFileContract, FaTruck, FaFolder,
  FaClipboardList, FaChartBar
} from "react-icons/fa";
import { MdDashboard } from "react-icons/md";

const MasterSideBar = ({ visible, onClose, onLogout, collapsed, setCollapsed }) => {
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [openSubMenus, setOpenSubMenus] = useState({});

  useEffect(() => {
    const fetchUser = () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          setUserData(parsed);
        }
      } catch (err) {
        console.error("Sidebar User Parse Error:", err);
      } finally {
        setLoadingUser(false);
      }
    };
    fetchUser();
  }, []);

  const rawRole = (userData?.role || "").toLowerCase();

  const toggleSubMenu = (label) => {
    setOpenSubMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // FIXED: Internal logout handler to ensure hard redirect on AWS
  const handleInternalLogout = () => {
    if (onLogout) {
      onLogout();
    } else {
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/login";
    }
  };

  // --- MENUS (HR, OA, PM, STAFF, HO, FINANCE, PD) ---
  const hrMenu = [
    { label: "Home", icon: <FiHome />, path: "/hr-dashboard" },
    { label: "Staff Profiles", icon: <FaUsers />, path: "/staff-dashboard" },
    { label: "Recruitment", icon: <FaUserTie />, path: "/recruitment" },
    { label: "Applicants", icon: <FaUsers />, path: "/applicants" },
    { label: "Performance", icon: <FaFileAlt />, path: "/performance" },
    { label: "Leave Management", icon: <FaUserCheck />, path: "/leave-requests" },
    { label: "Asset Inventory", icon: <FaWarehouse />, path: "/my-assets" },
    { label: "Payroll", icon: <FaMoneyBill />, path: "/payrolls" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/disciplinary" },
    { label: "Timesheets", icon: <FaFileAlt />, path: "/timesheets" },
    { label: "Contracts", icon: <FaFile />, path: "/contracts-page" },
    { label: "Documentation", icon: <FaFile />, path: "/documentation" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/hr-profile" },
  ];

  const oaMenu = [
    { label: "Home", icon: <FiHome />, path: "/operations-dashboard" },
    {
      label: "Procurement",
      icon: <FaTasks />,
      children: [
        { label: "Planning", icon: <FaFolder />, path: "/project-procurement-planning" },
        { label: "Requests & Orders", icon: <FaTasks />, path: "/oa/procurement" },
      ],
    },
    {
      label: "Requisition Mgmt",
      icon: <FaMoneyBillWave />,
      children: [
        { label: "All Requisitions", icon: <FaFileAlt />, path: "/requisitions" },
      ],
    },
    {
      label: "Vendors",
      icon: <FaFileContract />,
      children: [
        { label: "Profiles", icon: <FaUserCircle />, path: "/vendor-management" },
        { label: "Contracts", icon: <FaFileContract />, path: "/vendor-contracts" },
        { label: "Invoices", icon: <FaFileAlt />, path: "/vendor-invoices" },
      ],
    },
    { label: "Assets Mgmt", icon: <FiBox />, path: "/assets-management" },
    { label: "Inventory", icon: <FaFolder />, path: "/inventory-management" },
    { label: "Reports", icon: <FaFolder />, path: "/oa/reports" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/operations-discipline" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/oa/timesheet" },
    { label: "Documentation", icon: <FaFile />, path: "/oa/documentation" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/associate-profile" },
  ];

  const pmMenu = [
    { label: "Home", icon: <FiHome />, path: "/program-dashboard" },
    { label: "Projects", icon: <FiBriefcase />, path: "/pm/projects" },
    { label: "Activities", icon: <FiList />, path: "/pm/activitiespage" },
    { label: "Tasks", icon: <FiCheckSquare />, path: "/pm/tasks" },
    { label: "Procurement", icon: <FiFolder />, path: "/pm/procurement" },
    { label: "Asset Inventory", icon: <FaWarehouse />, path: "/program-assets" },
    { label: "Projects Inventory", icon: <FaWarehouse />, path: "/project-inventory" },
    { label: "Project Requests", icon: <FaFolderOpen />, path: "/project-requests" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/pm-discipline" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/pm/timesheet" },
    { label: "Documentation", icon: <FaFile />, path: "/pm/documentation" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/pm-profile" },
  ];

  const staffMenu = [
    { label: "Home", icon: <FiHome />, path: "/program-staff" },
    { label: "My Tasks", icon: <FaTasks />, path: "/my-tasks" },
    { label: "My Uploads", icon: <FaUpload />, path: "/my-uploads" },
    { label: "Procurement", icon: <FaMoneyBillWave />, path: "/my-procurement" },
    { label: "Asset Inventory", icon: <FaWarehouse />, path: "/field-assets" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/my-discipline" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/fo/timesheet" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/fo-profile" },
  ];

  const hoMenu = [
    { label: "Home", icon: <FiHome />, path: "/head-of-operations-dashboard" },
    { label: "Compliance Review", icon: <FaClipboardList />, path: "/project-compliance" },
    { label: "Risk Register", icon: <FiActivity />, path: "/risk-register" },
    { label: "Procurement Plans", icon: <FaTasks />, path: "/procurement-plans" },
    {
      label: "Vendor Mgmt",
      icon: <FaFileContract />,
      children: [
        { label: "Profiles", icon: <FaUserCircle />, path: "/vendor-managementHO" },
        { label: "Contracts", icon: <FaFileContract />, path: "/vendor-contractsHO" },
        { label: "Invoices", icon: <FaFileAlt />, path: "/vendor-invoicesHO" },
      ],
    },
    { label: "Assets Mgmt", icon: <FiBox />, path: "/assets-management" },
    { label: "Inventory", icon: <FaFolder />, path: "/inventory-management" },
    { label: "Equipment Mtc", icon: <FiBox />, path: "/equipment-maintenance" },
    { label: "Reports", icon: <FaChartBar />, path: "/hoc-reports" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/hoc-discipline" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/head-of-operations/timesheet" },
    { label: "Documentation", icon: <FaFile />, path: "/hoc-documentation" },
    { label: "My Profile", icon: <FiSettings />, path: "/hoc-settings" },
  ];

  const financeMenu = [
    { label: "Home", icon: <FiHome />, path: "/finance-dashboard" },
    { label: "Transactions", icon: <FiBriefcase />, path: "/finance-overview" },
    { label: "Asset Inventory", icon: <FaWarehouse />, path: "/finance-assets" },
    { label: "My Inventory", icon: <FiDatabase />, path: "/finance-inventory" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/finance-discipline" },
    { label: "Payroll", icon: <FaMoneyBill />, path: "/payrolls" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/finance-timesheet" },
    { label: "Documentation", icon: <FaFile />, path: "/finance-documentation" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/finance-profile" },
  ];

  const pdMenu = [
    { label: "Home", icon: <FiHome />, path: "/program-director" },
    { label: "Project Management", icon: <FiBriefcase />, path: "/projects" },
    { label: "Donor Management", icon: <FiUsers />, path: "/donor-management" },
    { label: "Leave Management", icon: <FaUserCheck />, path: "/leave-management" },
    { label: "Disciplinary", icon: <FaGavel />, path: "/disciplinary-management" },
    { label: "Timesheet", icon: <FaFileAlt />, path: "/director-timesheet" },
    { label: "Asset Inventory", icon: <FaWarehouse />, path: "/director-assets" },
    { label: "Procurement", icon: <FiFolder />, path: "/pd/procurement" },
    { label: "Documentation", icon: <FaFile />, path: "/document-management" },
    { label: "My Profile", icon: <FaUserCircle />, path: "/pd-profile" },
  ];

  // --- ROLE LOGIC ---
  let finalMenu = [];
  if (rawRole.includes("hr") || rawRole.includes("human")) {
    finalMenu = hrMenu;
  } else if (rawRole.includes("director") || rawRole === "pd") {
    finalMenu = pdMenu;
  } else if (rawRole.includes("finance")) {
    finalMenu = financeMenu;
  } else if (rawRole.includes("head") && (rawRole.includes("ops") || rawRole.includes("operations"))) {
    finalMenu = hoMenu;
  } else if (rawRole.includes("associate") || rawRole.includes("ops") || rawRole.includes("operations")) {
    finalMenu = oaMenu;
  } else if (rawRole.includes("staff") || rawRole.includes("field") || rawRole.includes("officer")) {
    finalMenu = staffMenu;
  } else {
    finalMenu = pmMenu; 
  }

  return (
    <>
      {visible && (
        <div className="fixed inset-0 bg-black/50 z-[40] md:hidden backdrop-blur-sm" onClick={onClose} />
      )}

      {/* FIXED: Increased z-index to z-[999] */}
      <aside className={`fixed top-0 left-0 h-full bg-white border-r border-gray-100 z-[30] transition-all duration-300 ease-in-out ${visible ? "translate-x-0" : "-translate-x-full md:translate-x-0"} ${collapsed ? "w-20" : "w-64"}`}>
        
        <div className="h-24 flex items-center justify-between px-4 border-b border-gray-50 bg-white">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <img src="/logo.jpeg" alt="Logo" className="w-64 object-contain" />
            </div>
          )}
          <button onClick={() => setCollapsed(!collapsed)} className="p-2 hover:bg-gray-100 rounded-lg text-gray-400 hidden md:block transition-colors">
            {collapsed ? <FiChevronRight size={20} /> : <FiChevronLeft size={20} />}
          </button>
        </div>

        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-200px)] custom-scrollbar">
          {finalMenu.map((item, index) => {
            const hasChildren = item.children && item.children.length > 0;
            const isSubOpen = openSubMenus[item.label];
            const isActive = location.pathname === item.path || (hasChildren && item.children.some(c => location.pathname === c.path));

            return (
              <div key={index} className="flex flex-col">
                <div 
                  className={`flex items-center justify-between px-3 py-3 rounded-xl transition-all duration-200 group cursor-pointer ${isActive ? "bg-orange-50 text-orange-600 shadow-sm" : "text-gray-500 hover:bg-gray-50"}`}
                  onClick={() => hasChildren ? toggleSubMenu(item.label) : null}
                >
                  <NavLink to={item.path || "#"} className="flex items-center gap-4 flex-1" onClick={(e) => hasChildren && e.preventDefault()}>
                    <span className={`text-xl transition-transform ${isActive ? "scale-110" : "group-hover:scale-110"}`}>{item.icon}</span>
                    {!collapsed && <span className={`font-bold text-sm truncate ${isActive ? "text-gray-900" : ""}`}>{item.label}</span>}
                  </NavLink>
                  {hasChildren && !collapsed && (
                    <span className="text-gray-400">
                      {isSubOpen ? <FiChevronUp /> : <FiChevronDown />}
                    </span>
                  )}
                </div>

                {hasChildren && isSubOpen && !collapsed && (
                  <div className="ml-9 mt-1 flex flex-col gap-1 border-l-2 border-orange-100 pl-4 animate-fadeIn">
                    {item.children.map((child, idx) => (
                      <NavLink
                        key={idx}
                        to={child.path}
                        className={({ isActive }) => `flex items-center gap-3 py-2 text-[11px] font-bold uppercase tracking-wider transition-colors ${isActive ? "text-orange-600" : "text-gray-400 hover:text-gray-700"}`}
                      >
                        <span className="text-sm">{child.icon}</span>
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 w-full bg-white border-t border-gray-50 p-4">
          <div className="flex flex-col gap-2">
            {!loadingUser && userData ? (
              <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : "px-2 mb-2"}`}>
                <FaUserCircle className="w-9 h-9 text-gray-300 flex-shrink-0" />
                {!collapsed && (
                  <div className="overflow-hidden">
                    <p className="text-sm font-black text-gray-800 truncate">{userData.name}</p>
                    <p className="text-[9px] font-bold text-gray-400 truncate uppercase tracking-tighter">{userData.email}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : "px-2 mb-2"}`}>
                <div className="w-9 h-9 bg-gray-100 rounded-full animate-pulse" />
                {!collapsed && <div className="h-3 w-24 bg-gray-100 rounded animate-pulse" />}
              </div>
            )}
            
            {/* FIXED: Using internal logout and added cursor-pointer */}
            <button 
              onClick={handleInternalLogout} 
              className={`flex items-center gap-4 w-full px-3 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-all group cursor-pointer ${collapsed ? "justify-center" : ""}`}
            >
              <FiLogOut className="text-xl group-hover:translate-x-1 transition-transform" />
              {!collapsed && <span className="font-bold text-sm">Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default MasterSideBar;